// IMP-151 AC-5: PCActivationPanel.js 의 비표현(non-presentational) 로직을 headless 훅으로 추출.
// 동작 불변(behavior-preserving) refactor — 로직/순서/deps/주석/dispatch/응답 shape/공개 props 변화 0.
// 유일 소비처 DealerIssuePanel.js 무영향.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { PcActivationAction } from '../../../actions/PcActivationAction';
import { Translate as t } from '../../../config/Translate';
import {
    isRenewalLicense,
    matchVoucherToRayTeamsUser,
    reportVoucherSsotDrift,
    resolveOtherLicenseInfo,
} from '../../../lib/licenseHelper';
// IMP-221: LICENSE_CODE_BADGES/getLicenseCodeBadge 는 lib/licenseCodes SSoT 로 이전.
import { getLicenseCodeBadge } from '../../../lib/licenseCodes';
// IMP-421 (#356): 실패 result → 표시 문구 결정 SSoT. PR #542 R2 리뷰 Major 로 본 파일 모듈
// 스코프에서 lib 로 이전 — LicenseDetailModal 도 같은 식을 공유한다.
import { resolvePcFailText } from '../../../lib/pcActivationResult';
import { isPcActivationEnabled } from '../../../lib/licenseProduct';
import { readVersionedBoolean, writeVersionedBoolean } from '../../../lib/sessionStorageVersioned';
// IMP-223: 결정 로직 SSoT — 분기 변수 도출을 순수 resolver 로 외부화.
import { resolvePcActivationState } from '../../../lib/pcActivationStateResolver';
// PR #236 review (CQ Minor): sessionStorage 키 상수화 — 오타/변경 시 한 곳만 수정.
// IMP-206: 버전 키 + 레거시 자동 마이그레이션 (pcActivation.specCheckDismissed → pcActivation:specCheckDismissed:v1).
const SPEC_CHECK_DISMISSED_KEY = 'pcActivation:specCheckDismissed:v1';
const SPEC_CHECK_DISMISSED_LEGACY_KEYS = ['pcActivation.specCheckDismissed'];
// PR #243 R2: VOUCHER_LICENSE_SK_PREFIX 는 lib/licenseHelper.js 로 이전 (IMP-226).
// 본 파일은 named import 로 참조. 기존 사용처 (sk pattern sanity check) 는 헬퍼 내부에
// 있고 외부 직접 사용은 없음 — 향후 다른 컴포넌트가 sk pattern 검증 시 동일 상수 참조.

// IMP-421 (#356): 실패 안내 문구는 `lib/pcActivationResult.resolvePcFailText(result, t)` SSoT.
// 본 파일의 실패 분기 4곳(handleActivate / handleDeactivate / handleDeactivateOtherAndActivate
// 의 선행 Deactivate + Activate)이 모두 같은 식을 경유한다.

// IMP-221: LICENSE_CODE_BADGES 상수 + getLicenseCodeBadge 함수는 lib/licenseCodes SSoT 로 이전.
// 자격 차단 코드(N0009/N0011/N0012) 판정은 IMP-223 으로 lib/pcActivationStateResolver 로 이전.

// 사용자 요청 (2026-05-14): noMatchedLicense alert 와 license-code-badge alert 두 분기
// 가 같은 도메인 상태(=PC 의 RAYTeams 계정에 사용 가능한 voucher/license 부재 안내)
// 인데도 ① variant 가 달라 배경색/폰트 색이 차이남 ② testid 두 개로 코드 중복.
// 단일 공용 렌더로 통합. matchedVoucher 부재는 virtual code 'no-match' 로 처리.
//
// IMP-225 (PR #240 review 22:07): i18n SSoT 통합.
//   matchedVoucher 부재 경로도 BE/FE 공용 SSoT 인 `pcActivation.code.N0003` 을 사용.
//   기존 `pcActivation.noMatchedLicense` 키는 deprecate. 두 SSoT 동기화 부담 해소.
//   `code: 'no-match'` virtual 식별자는 그대로 유지 — 진입 경로 구분 (observability).
//
// 'no-match' 는 LICENSE_CODE_BADGES 에 등록되지 않은 virtual 식별자 — `data-code` 속성
// 으로만 노출되며, BE/FE SSoT 인 BUG-113 의 ?purpose=check code 체계에 포함되지 않는다.
function resolveInfoBadge(matchedVoucher, localLicenseStatus) {
    // SSoT 우선순위 (Confluence "Local License Check API doc" 코드 명세 기준):
    //   1. localLicenseStatus.code — BE (rayteams localhost) 응답. RAYTeams 계정 기준
    //      voucher/license 상태를 가장 정확히 반영. matchedVoucher 부재 (=RAYGuard 사용자
    //      권한 스코프 외) 와 무관하게 사용.
    //   2. matchedVoucher 부재 + code 미매핑 (또는 localLicenseStatus null) —
    //      virtual 'no-match' fallback. "RAYTeams 계정에 voucher 없음" 의미가 아니라
    //      "현재 RAYGuard 에서 선택한 조직이 발급한 voucher 가 없음" 의미. BE 의
    //      pcActivation.code.N0003 ("No registered licenses") 와 의미 차원 분리.
    const badge = getLicenseCodeBadge(localLicenseStatus);
    if (badge) {
        return { ...badge, code: localLicenseStatus.code };
    }
    if (!matchedVoucher) {
        return {
            i18nKey: 'pcActivation.notIssuedBySelectedCompany',
            variant: 'info',
            code: 'no-match',
        };
    }
    return null;
}

// PR #240 review (Sec MAJOR CWE-200/359 + Sec minor CWE-1188/451 + CQ MAJOR):
// RAYTeams 계정 표시 정보(accountName / accountEmail) 결정 로직 외부화.
//
// 기존 인라인 5단계 fallback 의 문제:
//   1) rayteamsUser?.email 이 name 영역의 2순위 fallback 으로 들어가 raylink.json
//      변조 시 dealer 화면에 잘못된 customer 이름 노출 (UI Misrepresentation).
//   2) rayteamsUser.name 부재 + rayteamsUser.email 존재 케이스에서 name 영역과
//      email 영역에 같은 email 이 두 번 노출되어 시각적 중복.
//
// 해결:
//   - name fallback 에서 rayteamsUser.email 제거. email 은 항상 email 영역만.
//   - showEmailSeparately 가드로 동일 값 중복 출력 차단 (defense-in-depth).
//
// SSoT 우선순위 (변경 없음):
//   1~3순위: rayteamsUser (raylink.json — RAYTeams 계정의 SSoT)
//   4~5순위: matchedVoucher (BE 발급 시 입력값 — 표시명 fallback)
function resolveAccountIdentity(rayteamsUser, matchedVoucher) {
    const name = rayteamsUser?.name
        || matchedVoucher?.userid
        || matchedVoucher?.groupName
        || rayteamsUser?.userId
        || '-';
    const email = rayteamsUser?.email || matchedVoucher?.email || null;
    const showEmailSeparately = !!email && email !== name;
    return { name, email, showEmailSeparately };
}

export const usePcActivation = ({ dealerCustomerLicenses, companyMap = {}, product }) => {
    const dispatch = useDispatch();
    // FEAT-059: PC 활성화 미지원 제품(예: Surgical Guide)은 조회·로컬(localhost) 요청을 하지 않는다.
    // product 생략 = SMARTDent(기존 동작). 판정은 key 기준으로 메모해 인라인 객체 prop 에도 재판정·warn 반복이 없다.
    const productKey = product?.key;
    const hasProduct = product !== undefined && product !== null;
    const pcEnabled = useMemo(
        () => isPcActivationEnabled(
            hasProduct ? { key: productKey } : undefined,
            { handler: 'usePcActivation' },
        ),
        [hasProduct, productKey],
    );
    // thunk 에는 원본 product 를 넘긴다(해석·거부는 thunk 진입부). ref 로 두어 객체 ref 변동이 effect 를 재실행하지 않게 한다.
    const productRef = useRef(product);
    productRef.current = product;
    // IMP-169 (AC-2): connect(mapStateToProps) → useSelector 마이그레이션.
    // 필드별 개별 selector — 객체 반환(새 ref 매 렌더) 회피, 슬라이스 변경 시에만 재렌더.
    const rayteamsInstalled = useSelector((state) => state.PcActivationReducer.rayteamsInstalled);
    const rayteamsUser = useSelector((state) => state.PcActivationReducer.rayteamsUser);
    const localLicenseStatus = useSelector((state) => state.PcActivationReducer.localLicenseStatus);
    const pcActivations = useSelector((state) => state.PcActivationReducer.pcActivations);
    // AC-3 (IMP-151): MatchVoucherToRayTeamsUser thunk → useMemo 순수 매처로 대체.
    // Redux matchedVoucher 슬라이스는 불활성 보존(AC-6 정리 예정).
    const matchedVoucher = useMemo(
        () => matchVoucherToRayTeamsUser(rayteamsUser, dealerCustomerLicenses, localLicenseStatus?.sk),
        [rayteamsUser, dealerCustomerLicenses, localLicenseStatus?.sk],
    );
    const activationCount = useSelector((state) => state.PcActivationReducer.activationCount);
    const pcActivationLoading = useSelector((state) => state.PcActivationReducer.pcActivationLoading);
    const pcActivationError = useSelector((state) => state.PcActivationReducer.pcActivationError);
    const systemSpecCheck = useSelector((state) => state.PcActivationReducer.systemSpecCheck);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [selectedPcRtid, setSelectedPcRtid] = useState(null);
    // FEAT-034: 외부 scope dealer voucher 가 PC 의 RAYTeams 계정에 등록된 케이스의
    // 발급 조직 정보 (groupName + dealerId). BE PR #119 의 GET /voucher-license/meta
    // 호출 결과. 호출자 local state (dispatchRedux: false 패턴).
    const [externalVoucherMeta, setExternalVoucherMeta] = useState(null);
    // 사용자 요청 (2026-05-14): "Another license is currently active on this PC" 알림
    // (hasOtherLicenseInstalled) 에 닫기 버튼 추가. session 동안 dismiss 상태 유지 —
    // localLicenseStatus.sk 변경 시 reset (다른 라이선스 환경에서 다시 안내).
    const [occupiedByOtherDismissed, setOccupiedByOtherDismissed] = useState(false);
    useEffect(() => {
        // localLicenseStatus.sk 변경 = 라이선스 환경 변경 → dismiss 상태 reset.
        setOccupiedByOtherDismissed(false);
    }, [localLicenseStatus?.sk]);

    // RTSQA-339 round 17 (사용자 요청 2026-05-14): Activate/Deactivate 성공·실패 알림이
    // 15초 후 자동 사라짐. 사용자 명시적 dismiss 도 가능 (닫기 버튼).
    // message 가 새로 설정되면 타이머 재시작 (이전 타이머 cleanup). null 되면 노op.
    useEffect(() => {
        if (!message) return undefined;
        const timerId = setTimeout(() => setMessage(null), 15000);
        return () => clearTimeout(timerId);
    }, [message]);
    // PR #214 Round 2 (Arch Minor): unmount 후 setState 방지용 mounted ref.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    // IMP-194 (2026-05-12): PC spec check alert dismiss 상태 — 한 세션 동안 유지.
    // 사용자가 한 번 닫으면 같은 탭/세션 동안 다시 표시 안 함. 다음 세션엔 다시 평가.
    const [specCheckDismissed, setSpecCheckDismissed] = useState(
        () => readVersionedBoolean(SPEC_CHECK_DISMISSED_KEY, SPEC_CHECK_DISMISSED_LEGACY_KEYS),
    );
    const handleDismissSpecCheck = useCallback(() => {
        setSpecCheckDismissed(true);
        writeVersionedBoolean(SPEC_CHECK_DISMISSED_KEY, true);
    }, []);

    // 마운트 시 RAYTeams 설치/로그인 감지 + IMP-175 PC 권장 사양 체크 (1회만 실행).
    useEffect(() => {
        if (!pcEnabled) return;
        const init = async () => {
            const installed = await dispatch(PcActivationAction.CheckRayTeamsInstalled());
            if (!installed || !mountedRef.current) return;
            await dispatch(PcActivationAction.GetLocalRayTeamsUser());
            if (!mountedRef.current) return;
            // IMP-175: getinfo endpoint 호출. 실패 시 systemSpecCheck=null → silent (alert 미표시).
            dispatch(PcActivationAction.GetSystemSpecInfo());
        };
        init();
    }, [dispatch, pcEnabled]);

    // AC-3 Minor (PR #388): 원본 thunk _id+sk dedup 복원 — dealerCustomerLicenses refetch 등으로
    // 동일 voucher 가 새 ref 로 재계산돼도 동일 drift 상태(_id+sk+code)면 warn/breadcrumb 중복 방지.
    const lastDriftKeyRef = useRef(null);
    // AC-3 (IMP-151): SSoT drift 관측 — 기존 thunk 내부 부수효과를 useEffect 로 분리.
    useEffect(() => {
        const code = localLicenseStatus?.code;
        const driftKey = matchedVoucher ? `${matchedVoucher._id}:${matchedVoucher.sk}:${code}` : null;
        if (driftKey === lastDriftKeyRef.current) return;
        lastDriftKeyRef.current = driftKey;
        reportVoucherSsotDrift(matchedVoucher, code);
    }, [matchedVoucher, localLicenseStatus?.code]);

    // CheckLocalLicense — RAYTeams 로컬 라이선스 상태 조회 (rayteams localhost API).
    // 사용자 보고 (2026-05-14 round 3): rayteams localhost API 는 RAYTeams 사용자
    // (raylink.json) 기준으로 동작 — voucher 무관. 따라서 matchedVoucher 부재여도
    // 호출해야 외부 scope dealer voucher 가 이 PC 에 활성화된 케이스 (L0001/L0002/L0003)
    // 의 상태를 정확히 채울 수 있음. matchedVoucher 가드 안에서만 호출하면
    // localLicenseStatus 가 null 인 채로 머물러 N0003 fallback 으로 잘못 표시됨.
    // PR #243 review (CQ Minor): deps 에 rayteamsUser 객체 참조 대신 primitive (userId)
    // 사용 — useSelector 가 반환하는 객체 ref 변동 시 무관한 재호출을 방지.
    // PR #243 R2 (CQ+Arch Minor): 가드와 deps 의 granularity 통일 — 둘 다 `?.userId`
    // primitive 기준. rayteamsUser={userId:undefined} transient 에서 dispatch 누락 차단.
    useEffect(() => {
        if (!pcEnabled) return;
        if (!rayteamsUser?.userId) return;
        dispatch(PcActivationAction.CheckLocalLicense(productRef.current));
    }, [dispatch, pcEnabled, rayteamsUser?.userId]);

    // GetPCActivations — voucher sk 가 필요하므로 matchedVoucher 가드 유지.
    useEffect(() => {
        if (!pcEnabled) return;
        if (!matchedVoucher?._id) return;
        dispatch(PcActivationAction.GetPCActivations(matchedVoucher.sk, { product: productRef.current }));
    }, [dispatch, pcEnabled, matchedVoucher]);

    // FEAT-034: 외부 scope voucher meta 1회 fetch.
    //
    // 트리거 조건: matchedVoucher 부재 (RAYGuard cList 권한 외 voucher) + RAYTeams
    // 로그인 사용자의 userId/groupId 확보 시. localLicenseStatus 응답에 포함된
    // userId/groupId (Confluence "Local License Check API doc" 의 voucher_license
    // invariant) 를 그대로 BE 에 전달.
    //
    // 정상 케이스 (matchedVoucher 매칭) 는 dealerCustomerLicenses 의 정보로 충분하므로
    // 미트리거. silent fallback (404 / 네트워크 에러 → null).
    //
    // PR #244 review S-4 (Arch Suggestion): matchedVoucher 가 결정 전 (undefined) 일
    // 때는 transient 보류 — 불필요 BE 호출 1회 절약. `=== null` 명시 가드로 매칭 실패
    // 확정 시에만 fetch.
    useEffect(() => {
        if (!pcEnabled) {
            setExternalVoucherMeta(null);
            return;
        }
        if (matchedVoucher) {
            setExternalVoucherMeta(null);
            return;
        }
        if (matchedVoucher !== null) {
            // matchedVoucher === undefined — MatchVoucherToRayTeamsUser 결정 전. 보류.
            return;
        }
        const uid = localLicenseStatus?.userId;
        const gid = localLicenseStatus?.groupId;
        if (!uid || !gid) {
            setExternalVoucherMeta(null);
            return;
        }
        // cancelled flag — React cleanup 라이프사이클상 다음 effect 재실행 전에 cleanup
        // 이 먼저 실행되므로, matchedVoucher 전이 시 stale meta 가 setState 되지 않음.
        // PR 사전 리뷰 (Minor 2): React effect 순서가 보장되므로 race 위험 없음.
        let cancelled = false;
        (async () => {
            const meta = await dispatch(SmartDentAction.GetVoucherMetaByUser(uid, gid, { product: productRef.current }));
            if (!cancelled) setExternalVoucherMeta(meta);
        })();
        return () => { cancelled = true; };
    }, [dispatch, pcEnabled, matchedVoucher, localLicenseStatus?.userId, localLicenseStatus?.groupId]);

    // IMP-223: 모든 결정 분기를 순수 resolver(SSoT)에서 산출. 우선순위 명확화(설치/로그인/로딩
    // → unique-seat 한도 → code/seat/user 카드 플래그). 개념·버그이력은 resolver 주석 참조.
    // License(localLicenseStatus.check, PC 로컬 설치) 와 Seat(pcActivations, BE DDB 점유) 는 별개 —
    // Activate/Deactivate 는 Seat 기준. pcActivations 로딩/에러 시 판단 보류(false 강제 방지).
    const {
        uiState,
        action,
        activePcs,
        activeCount,
        uniqueLimitCount,
        isExternalScope,
        isActivatedForThisVoucher,
        seatFull,
        hasOtherLicenseInstalled,
        isLicenseStatusPending,
        isActivationUiVisible,
    } = resolvePcActivationState({
        rayteamsInstalled,
        rayteamsUser,
        localLicenseStatus,
        pcActivations,
        matchedVoucher,
        activationCount,
        pcActivationLoading,
        pcActivationError,
    });

    // 다른 라이선스가 이 PC 에 설치되어 있는 경우 (Seat 와는 무관, 안내 목적).
    // [Activate] 누르면 rayteams purpose=use 가 기존 라이선스를 교체 (replace).
    //
    // PR #214 Round 2 (Arch Major) — invariant: localLicenseStatus.sk (rayteams local) 와
    //   matchedVoucher.sk (BE voucher_license) 는 동일 sk 형식
    //   "lcuse:region:groupId:userId:type:guid" 를 공유한다.
    //   이는 rayteams 가 voucher_license.sk 를 license 활성화 시 그대로 저장하기 때문.
    //   이 invariant 가 깨지면 (예: rayteams 가 sk 정규화 다르게 저장) 잘못된 판단 가능.
    //   BE PR #110 의 sk 비교 로직이 동일 가정에 의존 — 정합성 유지 필요.
    // BUG-111 사용자 보고 (2026-05-12): isActivatedForThisVoucher 와 동시 true 가 되어
    // "활성화됨" + "다른 라이선스 설치" 두 메시지가 동시 출력되는 혼란.
    // 원인: 활성화 직후 localLicenseStatus 가 stale (CheckLocalLicense 비동기) 이거나,
    // 이 voucher 의 seat 가 used[] 에 있지만 PC 의 license 키는 다른 voucher 의 것인
    // corner case. 이미 이 voucher 로 활성화된 상태라면 "다른 voucher 점유" 안내는
    // 불요 — 의미적으로 isActivatedForThisVoucher 우선.
    //
    // BUG-111 review (Minor #3): pcActivationsReady=false 시 isActivatedForThisVoucher
    // 가 false 로 평가되어 transient 상태에서 hasOtherLicenseInstalled 가 잠시 true 표시
    // 회귀. pcActivationsReady 도 함께 가드 — used[] 정보 도착 후 판단.
    //
    // IMP-226 (2026-05-19): otherLicenseInfo 헬퍼 외부화 — lib/licenseHelper.js 의
    // resolveOtherLicenseInfo 순수 함수 호출. 4책임 (sk sanity check / name lookup /
    // typeLabel parsing / dealer 2-분기 CWE-200 정책) 모두 헬퍼 내부로 이동. 정책 변경
    // 시 헬퍼 단위 테스트로 검증 (src/lib/__tests__/resolveOtherLicenseInfo.test.js).
    // 컴포넌트는 useMemo 래핑 + deps 명시만 담당 — deps 누락 시 함수 호출 단계 명시.
    // PR #244 R1 review m-2: deps 첫 번째 — `localLicenseStatus?.sk` primitive 사용.
    // 헬퍼 내부에서 .sk 필드만 사용하므로 신호 정밀도 향상 (userId/groupId 등 무관 필드
    // 변경 시 불필요한 재계산 차단).
    // IMP-227: 헬퍼 param 을 `localLicenseStatusSk` 로 좁혀 이 deps(`localLicenseStatus?.sk`)와
    // param 이 1:1 일치 — stale closure 회귀 표면 제거.
    const otherLicenseInfo = useMemo(
        () => resolveOtherLicenseInfo({
            localLicenseStatusSk: localLicenseStatus?.sk,
            dealerCustomerLicenses,
            matchedVoucher,
            rayteamsUser,
            companyMap,
            externalVoucherMeta,
            isRenewalLicenseFn: isRenewalLicense,
            t,
        }),
        [localLicenseStatus?.sk, dealerCustomerLicenses, matchedVoucher, rayteamsUser, companyMap, externalVoucherMeta],
    );

    // 사용자 보고 (2026-05-14): 외부 dealer voucher 가 이 PC 의 RAYTeams 계정으로 이미
    // seat 활성화된 케이스 (matchedVoucher=null) 도 occupiedByOther alert 노출 대상.
    //
    // PR #243 review (Arch Minor): 두 케이스를 명시 표현으로 분리 — 의도 가독성 회복.
    //   - isExternalScope: matchedVoucher 부재. RAYGuard cList 스코프 외 dealer 발급 voucher.
    //   - isCrossVoucher: matchedVoucher 존재하지만 sk 가 PC 의 로컬 voucher 와 다름.
    //                     예: 같은 사용자의 initial/renewal 다중 voucher.
    //
    // PR #243 R2 (Arch Minor): `matchedVoucher` 객체 존재 + `.sk` falsy 케이스도 cover —
    // 이전 표현 `localLicenseStatus.sk !== matchedVoucher?.sk` 와 완전 동치 보장
    // (`localLicenseStatus.sk !== undefined` = true). matchedVoucher 가 set 되었으나
    // sk 만 비어있는 transient/BE 변동 케이스에서도 cross-voucher 감지가 손실되지 않음.
    // PR #243 R3 (CQ Suggestion): `!!matchedVoucher` 좌단축 평가 후 우변은 항상
    // matchedVoucher truthy — 우변 `?.sk` 는 잉여. `matchedVoucher.sk` 로 단순화하여
    // "가드 이후 영역" invariant 가 한 줄에서 더 명확히 드러남.
    // isExternalScope / hasOtherLicenseInstalled / isNotActivated / seatFull / uniqueLimitReached
    // 는 위 resolver 결과에서 destructure 됨 (IMP-223).

    // RTSQA-339 round 18 (사용자 요청 2026-05-14): Activate/Deactivate this PC 클릭 시 확인 후 진행.
    // IMP-388 FR-012: window.confirm → 공통 ModalConfirm. 핸들러는 확인 대기 상태(pendingConfirm)만
    // 열고, 확인 버튼(confirmPending)을 눌러야 run* 이 실행된다. 닫기/배경/ESC 는 cancelPending 만 호출(fail-closed).
    const [pendingConfirm, setPendingConfirm] = useState(null);

    const runActivate = useCallback(async () => {
        setLoading(true);
        setMessage(null);
        const result = await dispatch(PcActivationAction.ActivateCurrentPC(productRef.current));
        if (result?.ok) {
            setMessage({ type: 'success', text: t('pcActivation.activateSuccess') });
            dispatch(PcActivationAction.CheckLocalLicense(productRef.current));
            if (matchedVoucher?._id) dispatch(PcActivationAction.GetPCActivations(matchedVoucher.sk, { product: productRef.current }));
        } else {
            // IMP-421 (#356): 코드/사유 원문을 그대로 노출하던 것을 안내 문구로 교체(SSoT 헬퍼).
            setMessage({ type: 'danger', text: resolvePcFailText(result, t) });
        }
        setLoading(false);
    }, [dispatch, matchedVoucher]);

    const runDeactivate = useCallback(async () => {
        setLoading(true);
        setMessage(null);
        const result = await dispatch(PcActivationAction.DeactivateCurrentPC(productRef.current));
        if (result?.ok) {
            setMessage({ type: 'success', text: t('pcActivation.deactivateSuccess') });
            dispatch(PcActivationAction.CheckLocalLicense(productRef.current));
            if (matchedVoucher?._id) dispatch(PcActivationAction.GetPCActivations(matchedVoucher.sk, { product: productRef.current }));
        } else {
            // IMP-421: reason 원문(`NETWORK_ERROR` 등) 노출 해소 — code 를 반환하지 않는 경로라
            // reason 매핑만 적용된다(헬퍼가 code→reason→폴백 순서로 처리).
            setMessage({ type: 'danger', text: resolvePcFailText(result, t) });
        }
        setLoading(false);
    }, [dispatch, matchedVoucher]);

    const runDeactivateOtherAndActivate = useCallback(async () => {
        if (!selectedPcRtid || !matchedVoucher?._id || !matchedVoucher?.sk) return;
        setLoading(true);
        setMessage(null);
        // BUG-109: DeactivatePc 는 rayteams gateway 경유로 변경됨 (BE /pcs/deactivate 제거).
        // DDB sk 합성 필요 → matchedVoucher.sk 전달.
        const deactivResult = await dispatch(
            PcActivationAction.DeactivatePc(matchedVoucher.sk, selectedPcRtid, productRef.current),
        );
        if (!deactivResult?.ok) {
            // IMP-421 (PR #542 R2 리뷰 Major): 이 선행 분기만 reason 원문이 남아 `STALE_USED_LIST`
            // 같은 내부 토큰이 사용자에게 노출됐다. DeactivatePc 가 낼 수 있는 reason 6종은 전부
            // 매핑에 있으므로 code 없이 reason 매핑만으로 안내 문구가 결정된다.
            setMessage({ type: 'danger', text: resolvePcFailText(deactivResult, t) });
            setLoading(false);
            return;
        }
        const activateResult = await dispatch(PcActivationAction.ActivateCurrentPC(productRef.current));
        if (activateResult?.ok) {
            setMessage({ type: 'success', text: t('pcActivation.activateSuccess') });
            dispatch(PcActivationAction.CheckLocalLicense(productRef.current));
            dispatch(PcActivationAction.GetPCActivations(matchedVoucher.sk, { product: productRef.current }));
        } else {
            // IMP-421 (#356, PR #542 리뷰 Major): handleActivate 와 동일 thunk 실패이므로 같은
            // SSoT 헬퍼를 쓴다 — 종전에는 이 경로만 코드 원문(`N0011` 등)이 노출됐다.
            setMessage({ type: 'danger', text: resolvePcFailText(activateResult, t) });
        }
        setSelectedPcRtid(null);
        setLoading(false);
    }, [dispatch, matchedVoucher, selectedPcRtid]);

    // 클릭 핸들러: 확인 모달만 연다(실행 중이면 무시 — 기존 버튼 disabled 와 동일 의도).
    const handleActivate = useCallback(() => {
        if (loading) return;
        setPendingConfirm('activate');
    }, [loading]);
    const handleDeactivate = useCallback(() => {
        if (loading) return;
        setPendingConfirm('deactivate');
    }, [loading]);
    const handleDeactivateOtherAndActivate = useCallback(() => {
        if (loading) return;
        if (!selectedPcRtid || !matchedVoucher?._id || !matchedVoucher?.sk) return;
        setPendingConfirm('deactivateAndActivate');
    }, [loading, selectedPcRtid, matchedVoucher]);

    const cancelPending = useCallback(() => setPendingConfirm(null), []);
    // 확인 버튼: 대기 상태를 먼저 비워(모달 닫힘 + 중복 클릭 차단) 해당 동작 1회만 실행.
    const confirmPending = useCallback(() => {
        const kind = pendingConfirm;
        setPendingConfirm(null);
        if (loading) return;
        if (kind === 'activate') runActivate();
        else if (kind === 'deactivate') runDeactivate();
        else if (kind === 'deactivateAndActivate') runDeactivateOtherAndActivate();
    }, [pendingConfirm, loading, runActivate, runDeactivate, runDeactivateOtherAndActivate]);

    const infoBadge = resolveInfoBadge(matchedVoucher, localLicenseStatus);
    const { name: accountName, email: accountEmail, showEmailSeparately } =
        resolveAccountIdentity(rayteamsUser, matchedVoucher);

    return {
        // state machine (resolver)
        uiState,
        action,
        activePcs,
        activeCount,
        uniqueLimitCount,
        isExternalScope,
        hasOtherLicenseInstalled,
        isLicenseStatusPending,
        isActivationUiVisible,
        // 파생 표현값
        infoBadge,
        otherLicenseInfo,
        accountName,
        accountEmail,
        showEmailSeparately,
        // JSX 가 직접 읽는 raw bits
        systemSpecCheck,
        specCheckDismissed,
        message,
        loading,
        pcActivationLoading,
        selectedPcRtid,
        occupiedByOtherDismissed,
        // setter / 핸들러
        setSelectedPcRtid,
        dismissMessage: () => setMessage(null),
        dismissSpecCheck: handleDismissSpecCheck,
        dismissOccupiedByOther: () => setOccupiedByOtherDismissed(true),
        handleActivate,
        handleDeactivate,
        handleDeactivateOtherAndActivate,
        // IMP-388 FR-012: 확인 모달 상태/핸들러 (신규 필드)
        pendingConfirm,
        confirmPending,
        cancelPending,
    };
};
